import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import {
  Button,
  ButtonGroup,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  Flex,
  PopoverHeader,
  FormControl,
  FormErrorMessage,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { VideoIcon, MoreHorizontalIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useDukeTiptapEditor } from '../../useDukeTiptapEditor';

const videoUrlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
const youtubeRegex = /(https?:\/\/)?(www\.|m\.)?(youtube\.com|youtu\.?be)\/.+/; // cspell:disable-line

function VideoDialogReactComponent(props) {
  const { uploadFn } = useDukeTiptapEditor();
  const [videoUrl, setVideoUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [youtubeLinkError, setYoutubeLinkError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadFailed, setUploadFailed] = useState<boolean>(false);

  const handleFileChange = useCallback(
    (file: File | null) => {
      if (file === null) {
        return;
      }

      async function upload(file: File) {
        setUploading(true);

        if (uploadFn !== undefined) {
          try {
            const { filename, fileDrn } = await uploadFn(file);
            const currentPos = props.getPos();
            props.deleteNode();
            props.editor.commands.setVideo({ src: `/files/${filename}`, fileDrn }, currentPos);
          } catch (e) {
            setUploadFailed(true);
          } finally {
            setUploading(false);
          }
        }
      }

      upload(file);
    },
    [props, uploadFn],
  );

  if (!props.editor.options.editable) {
    return null;
  }

  const handleEmbedVideo = () => {
    if (!videoUrlRegex.test(videoUrl)) {
      setLinkError('Invalid link. Please enter a valid video link.');
      return;
    }
    setLinkError(null);
    const currentPos = props.getPos();
    props.deleteNode();
    props.editor.commands.setVideo({ src: videoUrl }, currentPos);
  };

  const handleYoutubeVideo = () => {
    if (!youtubeRegex.test(youtubeUrl)) {
      setYoutubeLinkError('Invalid youtube link. Please enter a valid link.');
      return;
    }
    setYoutubeLinkError(null);
    props.deleteNode();
    props.editor.commands.setYoutubeVideo({
      src: youtubeUrl,
      width: '100%',
    });
  };

  return (
    <ButtonGroup size="xl" isAttached variant="outline" colorScheme="gray" w="full">
      <Popover matchWidth placement="bottom-start">
        <PopoverTrigger>
          <Button flex={1} justifyContent="start" leftIcon={<VideoIcon />}>
            Add video
          </Button>
        </PopoverTrigger>
        <PopoverContent w="full">
          <PopoverCloseButton />
          <PopoverHeader>Select how you want to add an video</PopoverHeader>
          <PopoverBody>
            <Tabs isFitted>
              <TabList>
                {uploadFn !== undefined && <Tab>Upload</Tab>}
                <Tab>Embed link</Tab>
                <Tab>Youtube</Tab>
              </TabList>
              <TabPanels>
                {uploadFn !== undefined && (
                  <TabPanel>
                    <Flex direction="column" gap={4} justifyContent="center">
                      {uploading ? (
                        <Flex
                          direction="column"
                          gap={4}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Spinner size="md" color="brand.500" />
                          <Text size="sm">Uploading...</Text>
                        </Flex>
                      ) : (
                        <>
                          <FormControl isInvalid={uploadFailed}>
                            <input
                              type="file"
                              accept={'video/*'}
                              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                            />
                            <FormErrorMessage>Failed to upload video</FormErrorMessage>
                          </FormControl>
                          <Button onClick={() => handleFileChange(file)} size="sm">
                            Upload
                          </Button>
                        </>
                      )}
                    </Flex>
                  </TabPanel>
                )}
                <TabPanel>
                  <Flex direction="column" gap={4}>
                    <FormControl isInvalid={!!linkError}>
                      <Input
                        placeholder="Video link"
                        autoComplete="off"
                        spellCheck="false"
                        autoFocus
                        onChange={(e) => {
                          setLinkError(null);
                          setVideoUrl(e.target.value);
                        }}
                      />
                      <FormErrorMessage>{linkError}</FormErrorMessage>
                    </FormControl>
                    <Button size="sm" onClick={handleEmbedVideo}>
                      Embed video
                    </Button>
                  </Flex>
                </TabPanel>
                <TabPanel>
                  <Flex direction="column" gap={4}>
                    <FormControl isInvalid={!!youtubeLinkError}>
                      <Input
                        placeholder="Youtube video link"
                        autoComplete="off"
                        spellCheck="false"
                        autoFocus
                        onChange={(e) => {
                          setYoutubeLinkError(null);
                          setYoutubeUrl(e.target.value);
                        }}
                      />
                      <FormErrorMessage>{youtubeLinkError}</FormErrorMessage>
                    </FormControl>
                    <Button size="sm" onClick={handleYoutubeVideo}>
                      Embed Youtube video
                    </Button>
                  </Flex>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </PopoverBody>
        </PopoverContent>
      </Popover>
      <Menu>
        <MenuButton
          as={IconButton}
          icon={<Icon as={MoreHorizontalIcon} />}
          aria-label="video dialog options"
        />
        <MenuList>
          <MenuItem onClick={props.deleteNode}>Delete</MenuItem>
        </MenuList>
      </Menu>
    </ButtonGroup>
  );
}

function VideoDialogTiptapWrapper(props) {
  return (
    <NodeViewWrapper>
      <VideoDialogReactComponent {...props} />
    </NodeViewWrapper>
  );
}

export const VideoDialog = Node.create({
  name: 'video-dialog',

  draggable: true,

  group: 'block',

  atom: true,

  parseHTML() {
    return [
      {
        tag: 'video-dialog',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['video-dialog', mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer((props) => <VideoDialogTiptapWrapper {...props} />);
  },
});
